import * as React from 'react';
import Transition, { TransitionStatus } from 'react-transition-group/Transition';
import { useForkRef, usePrevious } from '../../hooks';
import { useHoneycombTheme } from '../../theming';
import { FixMeLater, getLanguageDirection, ReactElementWithRef } from '../../utils';
import { OptionMenuContext } from './OptionMenuContext';

export type OptionMenuViewportProps = {
    /**
     * The child `OptionMenu` component.
     */
    children: ReactElementWithRef<Element>;

    /**
     * A reference passed to the root element.
     */
    ref?: React.Ref<FixMeLater>;
};

function getTranslateValue(depth: number) {
    if (depth > 0) {
        const languageDirection = getLanguageDirection();

        let val = depth * 100;

        if (languageDirection === 'ltr') {
            val *= -1;
        }
        return `translate3d(${val}%, 0, 0)`;
    }

    return `translate3d(0, 0, 0)`;
}

export function OptionMenuViewport(props: Readonly<OptionMenuViewportProps>): React.JSX.Element {
    const { children, ref } = props;

    const { viewportDepth, animating, setAnimating } = React.useContext(OptionMenuContext);
    const prevViewportDepth = usePrevious(viewportDepth) || 0;

    const nodeRef = React.useRef<HTMLElement>(null);
    const handleRefIntermediary = useForkRef(children.ref, nodeRef);
    const handleRef = useForkRef(handleRefIntermediary, ref);

    const { theme } = useHoneycombTheme();
    const { duration, easing } = theme.transitions;
    const timeout = {
        enter: duration.enteringScreen,
        exit: duration.leavingScreen,
    };

    const handleEnter = (_isAppearing: boolean): void => {
        const { current: node } = nodeRef;
        if (!node) {
            return;
        }
        if (viewportDepth == null) {
            return;
        }

        const transition = `transform ${timeout.enter}ms ${easing.easeInOut}`;
        const transform = getTranslateValue(viewportDepth);

        node.style.transition = transition;
        node.style.transform = transform;
    };

    const handleEntered = (): void => {
        if (setAnimating) {
            // The animation has completed
            setAnimating(false);
        }
    };

    const handleExited = () => {
        const { current: node } = nodeRef;
        if (!node) {
            return;
        }
        node.style.transition = '';
    };

    React.useEffect(() => {
        if (viewportDepth !== prevViewportDepth) {
            // The viewport depth has just changed
            // We need to animate to the new depth.
            if (setAnimating) {
                setAnimating(true);
            }
        }
    }, [prevViewportDepth, setAnimating, viewportDepth]);

    return (
        <Transition
            nodeRef={nodeRef}
            in={animating}
            timeout={timeout}
            appear={true}
            onEnter={handleEnter}
            onEntered={handleEntered}
            onExited={handleExited}
        >
            {(_status: TransitionStatus, childProps: (Partial<FixMeLater> & React.Attributes) | undefined) => {
                return (
                    <div ref={handleRef} {...childProps}>
                        {children}
                    </div>
                );
            }}
        </Transition>
    );
}
